"use client";

import {
  DashboardRecordListView,
  DashboardRecordTitleCellView,
  DashboardRecordValueCellView,
  DashboardTextListView,
} from "@product/app/workspace/channels/[channelId]/record-dashboard-list-view";
import {
  BarWidget,
  BreakdownWidget,
  GroupedBarWidget,
  MetricWidget,
  SeriesWidget,
  StackedBarWidget,
} from "@product/app/workspace/channels/[channelId]/record-dashboard-saved-charts";
import {
  DashboardDateFilterView,
  DashboardFilterChipsView,
  DashboardGridItemView,
  DashboardLayoutGridView,
  DashboardSelectFilterView,
  DashboardWidgetHandlesView,
  DashboardWidgetTraceButton,
  DashboardWidgetsView,
  orderDashboardWidgets,
  RecordDashboardLensView,
} from "@product/app/workspace/channels/[channelId]/record-dashboard-view";
import { DeferredDashboardWidgetView } from "@product/app/workspace/channels/[channelId]/record-dashboard-deferred-widget";
import {
  RecordTableCellView,
  RecordTableGridView,
  RecordTableHeaderCellView,
  RecordTableHeaderRowView,
  RecordTableRowView,
} from "@product/app/workspace/channels/[channelId]/record-table-grid-view";
import { ROW_HEIGHT, TITLE_COLUMN_SIZE } from "@product/app/workspace/channels/[channelId]/record-table-state";
import { TableColumnResizeHandle } from "@product/components/ui/table-column-resize-handle";

import type { ShotDashboard, ShotDashboardFilter, ShotDashboardWidget } from "./channel-dashboard";
import { ShotCellValue } from "./channel-grid";

type RecordList = Extract<ShotDashboardWidget, { type: "record_list" }>;

/**
 * The record list's table at rest, as the dashboard's RecordTableGrid draws
 * it: a Record column then the widget's fields, each resizable, none
 * sortable, the titles as buttons that open the record.
 */
function RecordListTable({ widget }: { widget: RecordList }) {
  const widths = [TITLE_COLUMN_SIZE.size, ...widget.columns.map((column) => column.width)];
  const tableWidth = widths.reduce((sum, width) => sum + width, 0);
  const handle = (label: string) => <TableColumnResizeHandle aria-label={`Resize ${label}`} active={false} />;
  return (
    <RecordTableGridView
      caption={widget.title}
      tableWidth={tableWidth}
      bodyHeight={widget.rows.length * ROW_HEIGHT}
      header={
        <RecordTableHeaderRowView>
          <RecordTableHeaderCellView width={TITLE_COLUMN_SIZE.size} label="Record" name="Record" resizeHandle={handle("Record")} />
          {widget.columns.map((column) => (
            <RecordTableHeaderCellView
              key={column.key}
              width={column.width}
              label={column.label}
              name={column.label}
              resizeHandle={handle(column.label)}
            />
          ))}
        </RecordTableHeaderRowView>
      }
    >
      {widget.rows.map((row, index) => (
        <RecordTableRowView key={row.id} index={index} start={index * ROW_HEIGHT} tableWidth={tableWidth}>
          <RecordTableCellView width={TITLE_COLUMN_SIZE.size} role="gridcell" tabIndex={-1}>
            <DashboardRecordTitleCellView title={row.title} titleHint={row.title} openable />
          </RecordTableCellView>
          {widget.columns.map((column) => {
            const cell = row.cells[column.key]!;
            return (
              <RecordTableCellView key={column.key} width={column.width} role="gridcell" tabIndex={-1}>
                <DashboardRecordValueCellView hint={cell.display} numeric={column.numeric}>
                  <ShotCellValue cell={cell} />
                </DashboardRecordValueCellView>
              </RecordTableCellView>
            );
          })}
        </RecordTableRowView>
      ))}
    </RecordTableGridView>
  );
}

/** A widget's body as the saved widget renders it, with the trace action every widget carries. */
function WidgetBody({ widget }: { widget: ShotDashboardWidget }) {
  const actions = <DashboardWidgetTraceButton title={widget.title} />;
  if (widget.type === "metric") {
    return (
      <MetricWidget
        widget={{ title: widget.title, metric: { aggregate: widget.aggregate } }}
        data={{
          value: widget.value,
          ...(widget.previous !== undefined ? { previous: widget.previous } : {}),
          ...(widget.points ? { points: widget.points } : {}),
        }}
        displayKind={widget.displayKind}
        fieldLabel={widget.fieldLabel}
        description={widget.description}
        meta={widget.meta}
        actions={actions}
      />
    );
  }
  if (widget.type === "bar" || widget.type === "breakdown") {
    return widget.type === "breakdown" ? (
      <BreakdownWidget
        widget={{ title: widget.title, metric: { aggregate: widget.aggregate }, variant: widget.variant === "donut" ? "pie" : widget.variant }}
        data={widget.points}
        displayKind={widget.displayKind}
        seriesName={widget.seriesName}
        description={widget.description}
        meta={widget.meta}
        actions={actions}
      />
    ) : (
      <BarWidget
        widget={{ title: widget.title, metric: { aggregate: widget.aggregate }, variant: widget.variant }}
        data={widget.points}
        displayKind={widget.displayKind}
        seriesName={widget.seriesName}
        description={widget.description}
        meta={widget.meta}
        actions={actions}
      />
    );
  }
  if (widget.type === "time_series") {
    return (
      <SeriesWidget
        widget={{ title: widget.title, metric: { aggregate: widget.aggregate } }}
        data={widget.points}
        displayKind={widget.displayKind}
        seriesName={widget.seriesName}
        description={widget.description}
        meta={widget.meta}
        actions={actions}
      />
    );
  }
  if (widget.type === "grouped_bar") {
    return (
      <GroupedBarWidget
        widget={{ title: widget.title, metric: { aggregate: widget.aggregate }, variant: widget.variant }}
        data={widget.data}
        displayKind={widget.displayKind}
        description={widget.description}
        meta={widget.meta}
        actions={actions}
      />
    );
  }
  if (widget.type === "stacked_bar") {
    return (
      <StackedBarWidget
        widget={{ title: widget.title, metric: { aggregate: widget.aggregate } }}
        data={widget.data}
        displayKind={widget.displayKind}
        description={widget.description}
        meta={widget.meta}
        actions={actions}
      />
    );
  }
  if (widget.type === "insight_list" || widget.type === "risk_list") {
    return (
      <DashboardTextListView
        title={widget.title}
        description={widget.description}
        meta={widget.meta}
        actions={actions}
        items={widget.items}
        tone={widget.type === "risk_list" ? "risk" : "insight"}
      />
    );
  }
  if (widget.type !== "record_list") throw new Error(`No shot for a ${widget.type} widget.`);
  return (
    <DashboardRecordListView
      title={widget.title}
      description={widget.description}
      meta={widget.meta}
      actions={actions}
      openable
      records={widget.rows.map((row) => ({
        id: row.id,
        title: row.title,
        titleHint: row.title,
        cells: widget.columns.slice(0, 3).map((column) => {
          const cell = row.cells[column.key]!;
          return { key: column.key, label: column.label, hint: cell.display, numeric: column.numeric, value: <ShotCellValue cell={cell} /> };
        }),
      }))}
      table={<RecordListTable widget={widget} />}
    />
  );
}

/** The saved filters at rest, as DashboardFilterChips draws them: nothing chosen, so no filter applies. */
function ShotDashboardFilters({ filters }: { filters: readonly ShotDashboardFilter[] }) {
  return (
    <DashboardFilterChipsView>
      {filters.map((filter) =>
        filter.type === "date" ? (
          <DashboardDateFilterView key={filter.id} title={filter.title} label={filter.label} />
        ) : (
          <DashboardSelectFilterView key={filter.id} title={filter.title} allLabel={filter.allLabel} options={filter.options} />
        ),
      )}
    </DashboardFilterChipsView>
  );
}

/**
 * A saved dashboard view at rest, as RecordDashboardLens draws it: its saved
 * filters, then the widgets in reading order on the layout's grid, each in
 * its deferred slot (shown) with the arrange handles a saved view carries.
 */
export function ShotDashboardView({ dashboard }: { dashboard: ShotDashboard }) {
  return (
    <RecordDashboardLensView
      gap={dashboard.layout.gap}
      filters={dashboard.filters.length ? <ShotDashboardFilters filters={dashboard.filters} /> : undefined}
    >
      <DashboardWidgetsView>
        <DashboardLayoutGridView layout={dashboard.layout} arrangeable>
          {orderDashboardWidgets(dashboard.widgets).map((widget) => (
            <DashboardGridItemView key={widget.id} position={widget.position} metric={widget.type === "metric"}>
              <DeferredDashboardWidgetView title={widget.title} visible>
                <WidgetBody widget={widget} />
              </DeferredDashboardWidgetView>
              <DashboardWidgetHandlesView title={widget.title} />
            </DashboardGridItemView>
          ))}
        </DashboardLayoutGridView>
      </DashboardWidgetsView>
    </RecordDashboardLensView>
  );
}
